import { Component, Input, OnInit } from '@angular/core'
import { ShareOptions } from 'src/app/interfaces/main'
import { QuizReportType } from 'src/app/interfaces/quiz'
import { GeneralService } from 'src/app/services/general/general.service'
import { LogService } from 'src/app/services/log/log.service'
import { SnackbarService } from 'src/app/services/snackbar/snackbar.service'
import { LogEvent } from './../../../interfaces/log'

export interface WebShareData {
  title?: string
  text?: string
  url: string
}

@Component({
  selector: 'app-sub-share',
  templateUrl: './sub-share.component.html',
  styleUrls: ['./sub-share.component.scss'],
})
export class SubShareComponent implements OnInit {
  @Input() text: string = ''
  @Input() url: string = ''
  @Input() quizType: QuizReportType | undefined
  @Input() additionalParams = ''
  @Input() totalReplyCount: number | undefined

  quizReportType = QuizReportType
  private shareLog: SubShareLog | undefined

  constructor(
    private snackbarService: SnackbarService,
    private logService: LogService,
    private generalService: GeneralService
  ) {}

  ngOnInit(): void {
    // 마지막에 enter 입력해줘야함. 안그러면 text와 url이 붙어서 다닌다.
    // enter를 주기 위해 추가적인 처리.
    if (this.text) {
      this.text = `${this.text}
`
    }
    if (this.quizType !== undefined) {
      this.shareLog = this.shareLogFactory(this.quizType)
    }
  }

  async shareUsingWebApi(): Promise<void> {
    const shareOptions: ShareOptions = {
      text: this.text,
      url: `https://${this.url}?utm_medium=share_btn${this.additionalParams}`,
    }
    this.generalService.share(shareOptions)
    this.shareLog?.logShareWebApiClicked()
  }

  async copyUrl(): Promise<void> {
    try {
      await this.generalService.copy(`${this.url}?utm_medium=copy_link${this.additionalParams}`)
      this.snackbarService.openSnackBar('클립보드에 링크가 복사되었습니다.')
    } catch (error) {
      console.error(error)
      this.snackbarService.openSnackBar('링크 복사에 실패하였습니다.')
    }
    this.shareLog?.logCopyClicked()
  }

  private shareLogFactory(quizReportType: QuizReportType): SubShareLog | undefined {
    if (quizReportType === QuizReportType.MBTI) {
      return new MbtiSubShareLog(this.logService, this.totalReplyCount ?? 0)
    }
    if (quizReportType === QuizReportType.FAVOUR_TOURNAMENT) {
      return new FTSubShareLog(this.logService, this.totalReplyCount ?? 0)
    }
    if (quizReportType === QuizReportType.THINKING_CHAIR) {
      return new ThinkingChairSubShareLog(this.logService)
    }
    if (quizReportType === QuizReportType.ATTACHMENT_TYPE) {
      return new AttachmentQuizSubShareLog(this.logService, this.totalReplyCount ?? 0)
    }
    if (quizReportType === QuizReportType.GUESS_ME) {
      return new GuessMeSubShareLog(this.logService, this.totalReplyCount ?? 0)
    }
  }
}

abstract class SubShareLog {
  constructor(protected logService: LogService) {}
  abstract logCopyClicked(): void
  abstract logShareWebApiClicked(): void
}

class MbtiSubShareLog extends SubShareLog {
  constructor(protected logService: LogService, private totalReplyCount: number) {
    super(logService)
  }

  logCopyClicked(): void {
    const event = LogEvent.MBTI_QUIZ_SHARE_COPY
    const eventParams = {
      reply_on_this_quiz: this.totalReplyCount,
      category: 'mbti_quiz_host_quiz',
    }
    this.logService.eventLogging(event, eventParams)
  }
  logShareWebApiClicked(): void {
    const event = LogEvent.MBTI_QUIZ_SHARE_WEB_API
    const eventParams = {
      reply_on_this_quiz: this.totalReplyCount,
      category: 'mbti_quiz_host_quiz',
    }
    this.logService.eventLogging(event, eventParams)
  }
}

class FTSubShareLog extends SubShareLog {
  constructor(protected logService: LogService, private totalReplyCount: number) {
    super(logService)
  }

  logCopyClicked(): void {
    const event = LogEvent.FW_QUIZ_SHARE_COPY
    const eventParams = {
      reply_on_this_quiz: this.totalReplyCount,
      category: 'fw_host_quiz',
    }
    this.logService.eventLogging(event, eventParams)
  }
  logShareWebApiClicked(): void {
    const event = LogEvent.FW_QUIZ_SHARE_WEB_API
    const eventParams = {
      reply_on_this_quiz: this.totalReplyCount,
      category: 'fw_host_quiz',
    }
    this.logService.eventLogging(event, eventParams)
  }
}

class ThinkingChairSubShareLog extends SubShareLog {
  constructor(protected logService: LogService) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.tc.thinkingChairHostCreateShareURLClicked()
  }
  logShareWebApiClicked(): void {
    this.logService.tc.thinkingChairHostCreateShareWebShareClicked()
  }
}

class AttachmentQuizSubShareLog extends SubShareLog {
  constructor(protected logService: LogService, private totalReplyCount: number) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.attachment.logAttachmentQuizHostQuizDetailShareURLClicked(this.totalReplyCount)
  }
  logShareWebApiClicked(): void {
    this.logService.attachment.logAttachmentQuizHostQuizDetailShareWebshareclicked(this.totalReplyCount)
  }
}

class GuessMeSubShareLog extends SubShareLog {
  constructor(protected logService: LogService, private totalReplyCount: number) {
    super(logService)
  }

  logCopyClicked(): void {
    this.logService.guessMe.logGuessMeHostQuizDetailShareURLClicked(this.totalReplyCount)
  }
  logShareWebApiClicked(): void {
    this.logService.guessMe.logGuessMeHostQuizDetailShareWebshareClicked(this.totalReplyCount)
  }
}
